import { useEffect, useRef } from "react";

interface PWANavigationGuardOptions {
  /** Whether to prevent back navigation when in PWA mode */
  preventBack?: boolean;
  /** Custom callback when back navigation is attempted */
  onBackAttempt?: () => void;
}

/**
 * Hook to prevent unwanted navigation in PWA mode
 * Useful for preventing users from navigating back to login pages
 * when they're already authenticated
 */
export function usePWANavigationGuard(options: PWANavigationGuardOptions = {}) {
  const { preventBack = true, onBackAttempt } = options;
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!preventBack) return;

    // Detect if we're in PWA/standalone mode
    const isPWA = 
      // Standard PWA detection
      window.matchMedia('(display-mode: standalone)').matches || 
      // iOS Safari PWA detection
      (window.navigator as any).standalone === true ||
      // Android Chrome PWA detection
      document.referrer.includes('android-app://') ||
      // Additional check for Chrome PWA
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches;

    if (!isPWA) return; // Only apply in PWA mode

    const handlePopState = (event: PopStateEvent) => {
      // Prevent the default back navigation
      event.preventDefault();
      
      // Call custom callback if provided
      if (onBackAttempt) {
        onBackAttempt();
      }

      // Push current state again to prevent going back
      window.history.pushState(null, '', window.location.href);
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Additional safety net - prevent navigation attempts
      if (isPWA) {
        event.preventDefault();
        return '';
      }
    };

    // Add event listeners
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Initialize history state only once per session
    if (!isInitialized.current) {
      // Clear any existing history first
      window.history.replaceState(null, '', window.location.href);
      // Push multiple states to create a buffer
      for (let i = 0; i < 3; i++) {
        window.history.pushState(null, '', window.location.href);
      }
      isInitialized.current = true;
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [preventBack, onBackAttempt]);
}